// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { serializeInvoiceLineItemFilter } from '@/billing/api/get-invoice-line-items';
import {
  CurrencyInvoiceLineItemSummary,
  InvoiceLineItemListItem,
  InvoiceLineItemSortProperty,
  invoiceLineItemSortPropertySchema,
} from '@/billing/api/types';
import { BILLING_BREADCRUMB_ROOT } from '@/billing/components/billing-nav';
import {
  BILL_DATE_SHORTCUTS,
  PAGE_SIZE,
} from '@/billing/components/charge-status-constants';
import { PerCurrencySections } from '@/billing/components/currency-section-accordion';
import { useInvoiceLineItems } from '@/billing/hooks/use-invoice-line-items';
import { DateRangeInput } from '@/components/date-range-input';
import { EmptyState } from '@/components/empty-state';
import { FilterMenu } from '@/components/filter-menu';
import { InvoiceStatusChip } from '@/components/invoice-status-chip';
import { Link } from '@/components/link';
import { PageContent } from '@/components/page-content';
import { SectionBreadcrumbs } from '@/components/section-breadcrumbs';
import {
  MetricDivider,
  MetricTable,
  MetricTableRow,
  PrimaryMetric,
  SummaryCard,
} from '@/components/summary-card';
import { TableControlContainer } from '@/components/table-control-container';
import { TablePaginationFooter } from '@/components/table-pagination-footer';
import {
  ArrowForwardRounded,
  ClearRounded,
  SearchRounded,
} from '@/constants/hardfin-standard-icons';
import { SEARCH_TERM_MAXIMUM_CHARACTERS } from '@/constants/search-term';
import {
  Sort,
  SortDirection,
  invert,
  sortDirectionSchema,
} from '@/constants/sort';
import { formatCurrencyValue } from '@/currency/utilities/format';
import { CustomerFilterMenu } from '@/customer/components/customer-filter-menu';
import { useTableSort } from '@/hooks/use-table-sort';
import { getSparseProjects } from '@/project/api/get-sparse-projects';
import { formatShortDate } from '@/utilities/date-time';
import { pluralize } from '@/utilities/pluralize';
import { searchTermSchema } from '@/utilities/search-term';
import {
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DateRange } from '@mui/x-date-pickers-pro';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Decimal } from 'decimal.js';
import { omit } from 'lodash-es';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useRef, useState } from 'react';
import { z } from 'zod';

const searchSchema = z.object({
  customerKeys: z.array(z.string()).optional(),
  projectKeys: z.array(z.string()).optional(),
  query: searchTermSchema,
  page: z.number().optional(),
  invoiceDateStart: z.string().optional().catch(undefined),
  invoiceDateEnd: z.string().optional().catch(undefined),
  sortBy: invoiceLineItemSortPropertySchema.optional().catch(undefined),
  sortDirection: sortDirectionSchema.optional().catch(undefined),
});

export const Route = createFileRoute('/_app/billing/invoice-line-items')({
  component: InvoiceLineItemsRoute,
  validateSearch: searchSchema,
});

const COLUMN_COUNT = 10;

// When the URL has no explicit invoice-date filter, default the view to the
// trailing 90 days. Path A frames this report as a billing-detail surface
// that ladders into rev rec — both use cases are period-scoped, and an
// unbounded default makes the summary aggregates (subtotal, status mix)
// meaningless at scale. The default is soft: clearing filters drops it,
// setting any explicit range overrides it.
const DEFAULT_DATE_RANGE_DAYS = 90;

function InvoiceLineItemsRoute() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const sort: Sort<InvoiceLineItemSortProperty> = useMemo(
    () => ({
      property: search.sortBy ?? 'invoiceDate',
      direction: search.sortDirection ?? 'desc',
    }),
    [search.sortBy, search.sortDirection]
  );
  const { getSortDirection, getLabelDirection, isLabelActive } =
    useTableSort(sort);

  const activeCustomerKeys = search.customerKeys ?? [];
  const activeProjectKeys = search.projectKeys ?? [];
  const page = search.page ?? 1;

  // Effective date range: explicit URL params if present, otherwise the
  // trailing DEFAULT_DATE_RANGE_DAYS default. The effective range is what
  // the picker shows AND what gets sent to the backend, so the user
  // never sees a "no scope" state unless they actively clear a date filter.
  const effectiveInvoiceDateStart =
    search.invoiceDateStart ??
    DateTime.now().minus({ days: DEFAULT_DATE_RANGE_DAYS }).toISODate() ??
    undefined;
  const effectiveInvoiceDateEnd =
    search.invoiceDateEnd ?? DateTime.now().toISODate() ?? undefined;
  const invoiceDateRange: DateRange<DateTime> = [
    effectiveInvoiceDateStart
      ? DateTime.fromISO(effectiveInvoiceDateStart)
      : null,
    effectiveInvoiceDateEnd ? DateTime.fromISO(effectiveInvoiceDateEnd) : null,
  ];

  const lineItemFilter = {
    pageSize: PAGE_SIZE,
    pageNumber: page,
    sort,
    search: search.query,
    customerKeys:
      activeCustomerKeys.length > 0 ? activeCustomerKeys : undefined,
    projectKeys: activeProjectKeys.length > 0 ? activeProjectKeys : undefined,
    invoiceDateStart: effectiveInvoiceDateStart,
    invoiceDateEnd: effectiveInvoiceDateEnd,
  };

  const { lineItems, totalCount, summary, isPlaceholderData } =
    useInvoiceLineItems({
      filter: lineItemFilter,
    });

  const { data: projectsData } = useQuery({
    queryKey: ['sparse-projects', 'invoice-line-items-filter-options'],
    queryFn: getSparseProjects,
  });
  const projectOptions = (projectsData?.projects ?? []).map((p) => ({
    id: p.key,
    label: p.name,
    isActive: activeProjectKeys.includes(p.key),
  }));

  const lastPage =
    totalCount === null ? 1 : Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  useEffect(() => {
    if (totalCount !== null && totalCount > 0 && page > lastPage) {
      void navigate({ search: { ...omit(search, 'page') } });
    }
  }, [page, lastPage, totalCount, navigate, search]);

  const [searchInput, setSearchInput] = useState(search.query ?? '');
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchRef = useRef(search);
  searchRef.current = search;

  useEffect(() => {
    if (searchTimeoutRef.current === null) {
      setSearchInput(search.query ?? '');
    }
  }, [search.query]);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current !== null) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const createSortHandler = (property: InvoiceLineItemSortProperty) => () => {
    const nextDirection =
      sort.property === property ? invert(sort.direction) : 'asc';
    void navigate({
      search: {
        ...search,
        sortBy: property,
        sortDirection: nextDirection,
        page: undefined,
      },
    });
  };

  const handleChangeCustomer = (customerKeys: string[]) => {
    void navigate({
      search:
        customerKeys.length === 0
          ? { ...omit(search, 'customerKeys', 'page') }
          : { ...search, customerKeys, page: undefined },
    });
  };

  const handleChangeProject = (projectKeys: string[]) => {
    void navigate({
      search:
        projectKeys.length === 0
          ? { ...omit(search, 'projectKeys', 'page') }
          : { ...search, projectKeys, page: undefined },
    });
  };

  const handleChangeInvoiceDateRange = (dateRange: DateRange<DateTime>) => {
    void navigate({
      search: {
        ...omit(search, ['invoiceDateStart', 'invoiceDateEnd', 'page']),
        invoiceDateStart: dateRange[0]?.toISODate() ?? undefined,
        invoiceDateEnd: dateRange[1]?.toISODate() ?? undefined,
      },
    });
  };

  const handleSearch = (query: string) => {
    setSearchInput(query);
    if (searchTimeoutRef.current !== null) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      searchTimeoutRef.current = null;
      const latestSearch = searchRef.current;
      void navigate({
        search:
          query.length === 0
            ? { ...omit(latestSearch, 'query', 'page') }
            : { ...latestSearch, query, page: undefined },
      });
    }, 300);
  };

  const handlePageChange = (newPage: number) => {
    void navigate({
      search:
        newPage === 1
          ? { ...omit(search, 'page') }
          : { ...search, page: newPage },
    });
  };

  // Date range lives in the header (always populated, defaults to trailing
  // 90 days), so it's intentionally excluded from the filter-row active
  // state and from the Clear filters action. Users adjust date via the
  // header picker, not the filter row.
  const hasActiveFilters =
    activeCustomerKeys.length > 0 ||
    activeProjectKeys.length > 0 ||
    (search.query ?? '').length > 0;

  const exportFilter = {
    ...lineItemFilter,
    pageSize: -1,
    pageNumber: undefined,
  };
  const serializedExportFilter = serializeInvoiceLineItemFilter(exportFilter);
  const exportURL = new URL(
    serializedExportFilter
      ? `/v1/export/invoice-line-items?${serializedExportFilter}`
      : '/v1/export/invoice-line-items',
    import.meta.env.VITE_API_URL as string
  );

  return (
    <PageContent>
      <Stack sx={{ mb: 2 }}>
        <SectionBreadcrumbs
          items={[BILLING_BREADCRUMB_ROOT, { label: 'Invoice line items' }]}
        />
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="h5">Invoice line items</Typography>
          <Stack direction="row" alignItems="center" spacing={2}>
            <DateRangeInput
              label="Invoice date"
              width="240px"
              value={invoiceDateRange}
              onChange={handleChangeInvoiceDateRange}
              shortcuts={BILL_DATE_SHORTCUTS}
            />
            <Button
              component={Link}
              to="/billing/invoices"
              size="small"
              endIcon={<ArrowForwardRounded fontSize="small" />}
              sx={{ textTransform: 'none' }}
            >
              View invoices
            </Button>
          </Stack>
        </Stack>
      </Stack>

      {summary === null ? (
        <Box sx={{ mb: 2 }}>
          <CurrencyLineItemTiles bucket={null} />
        </Box>
      ) : (
        <Box sx={{ mb: 2 }}>
          <PerCurrencySections
            buckets={summary.byCurrency}
            renderBucket={(bucket) => <CurrencyLineItemTiles bucket={bucket} />}
          />
        </Box>
      )}

      <Stack spacing={2}>
        <TableControlContainer paddingTop={0}>
          <Stack direction="row" spacing={2} flexGrow={1} alignItems="center">
            <CustomerFilterMenu
              label="Customer"
              identifier="key"
              onlyCustomers
              includeNotSet
              value={activeCustomerKeys}
              onChange={handleChangeCustomer}
            />
            <FilterMenu
              label="Project"
              options={projectOptions}
              onChangeOption={(id, isActive) => {
                handleChangeProject(
                  isActive
                    ? [...activeProjectKeys, id]
                    : activeProjectKeys.filter((p) => p !== id)
                );
              }}
            />
            {hasActiveFilters && (
              <Button
                size="small"
                onClick={() => {
                  setSearchInput('');
                  void navigate({
                    search: omit(search, [
                      'customerKeys',
                      'projectKeys',
                      'query',
                      'page',
                    ]),
                  });
                }}
              >
                Clear filters
              </Button>
            )}
          </Stack>
          <TextField
            size="small"
            placeholder="Search line items"
            value={searchInput}
            inputProps={{ maxLength: SEARCH_TERM_MAXIMUM_CHARACTERS }}
            onChange={(e) => handleSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRounded
                    fontSize="small"
                    sx={{ color: 'neutral.400' }}
                  />
                </InputAdornment>
              ),
              endAdornment: searchInput ? (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => handleSearch('')}
                    edge="end"
                    size="small"
                  >
                    <ClearRounded />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
            sx={{ width: { xs: '100%', sm: '20rem' } }}
          />
        </TableControlContainer>

        {lineItems !== null && lineItems.length === 0 && !isPlaceholderData ? (
          <EmptyState title="No invoice line items found" />
        ) : (
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ mt: '0 !important' }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width="10%">Invoice</TableCell>
                  <SortableHeader
                    label="Customer"
                    sortProperty="customer"
                    width="11%"
                    getSortDirection={getSortDirection}
                    getLabelDirection={getLabelDirection}
                    isLabelActive={isLabelActive}
                    onSort={createSortHandler}
                  />
                  <SortableHeader
                    label="Project"
                    sortProperty="project"
                    width="14%"
                    getSortDirection={getSortDirection}
                    getLabelDirection={getLabelDirection}
                    isLabelActive={isLabelActive}
                    onSort={createSortHandler}
                  />
                  <SortableHeader
                    label="Line item"
                    sortProperty="description"
                    width="14%"
                    getSortDirection={getSortDirection}
                    getLabelDirection={getLabelDirection}
                    isLabelActive={isLabelActive}
                    onSort={createSortHandler}
                  />
                  <SortableHeader
                    label="Item"
                    sortProperty="item"
                    width="10%"
                    getSortDirection={getSortDirection}
                    getLabelDirection={getLabelDirection}
                    isLabelActive={isLabelActive}
                    onSort={createSortHandler}
                  />
                  <SortableHeader
                    label="Amount"
                    sortProperty="total"
                    width="8%"
                    align="right"
                    getSortDirection={getSortDirection}
                    getLabelDirection={getLabelDirection}
                    isLabelActive={isLabelActive}
                    onSort={createSortHandler}
                  />
                  <TableCell width="7%" align="right">
                    Tax
                  </TableCell>
                  <SortableHeader
                    label="Service period"
                    sortProperty="servicePeriodStart"
                    width="12%"
                    getSortDirection={getSortDirection}
                    getLabelDirection={getLabelDirection}
                    isLabelActive={isLabelActive}
                    onSort={createSortHandler}
                  />
                  <SortableHeader
                    label="Sent"
                    sortProperty="sentDate"
                    width="7%"
                    getSortDirection={getSortDirection}
                    getLabelDirection={getLabelDirection}
                    isLabelActive={isLabelActive}
                    onSort={createSortHandler}
                  />
                  <SortableHeader
                    label="Paid"
                    sortProperty="paidDate"
                    width="7%"
                    getSortDirection={getSortDirection}
                    getLabelDirection={getLabelDirection}
                    isLabelActive={isLabelActive}
                    onSort={createSortHandler}
                  />
                </TableRow>
              </TableHead>
              <TableBody>
                {lineItems === null ||
                (lineItems.length === 0 && isPlaceholderData)
                  ? Array.from({ length: PAGE_SIZE }).map((_, rowIndex) => (
                      <TableRow key={`skeleton-${rowIndex}`}>
                        {Array.from({ length: COLUMN_COUNT }).map(
                          (__, colIndex) => (
                            <TableCell key={colIndex}>
                              <Skeleton variant="text" />
                            </TableCell>
                          )
                        )}
                      </TableRow>
                    ))
                  : lineItems.map((lineItem) => (
                      <InvoiceLineItemRow
                        key={lineItem.id}
                        lineItem={lineItem}
                      />
                    ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <TablePaginationFooter
          pagination={{
            page,
            pageSize: PAGE_SIZE,
            onPageChange: handlePageChange,
          }}
          totalCount={totalCount}
          entityOptions={{ singular: 'line item', includeCount: false }}
          downloadURL={exportURL}
          downloadLabel="Export invoice line items"
        />
      </Stack>
    </PageContent>
  );
}

// Renders the three report tiles (line items, amount, posted + status mix)
// for a single invoice currency. The report breaks figures out per currency
// since there is no meaningful cross-currency total; single-currency tenants
// (the common case) see exactly one group. A `null` bucket signals the
// loading state — every tile renders a skeleton and must never show a
// fabricated zero, since $0 is itself a meaningful business state.
export function CurrencyLineItemTiles(props: {
  bucket: CurrencyInvoiceLineItemSummary | null;
}) {
  const { bucket } = props;

  function tileValue(amount: string): string {
    return bucket
      ? formatCurrencyValue({
          amount: new Decimal(amount),
          isoCode: bucket.isoCode,
        })
      : '';
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={4}>
        <SummaryCard>
          <PrimaryMetric
            label="Line items"
            value={bucket === null ? '' : bucket.lineItemCount.toLocaleString()}
            color="primary.main"
            isLoading={bucket === null}
            caption={
              bucket === null
                ? undefined
                : pluralize(bucket.invoiceCount, { singular: 'invoice' })
            }
          />
        </SummaryCard>
      </Grid>
      <Grid item xs={12} md={4}>
        <SummaryCard>
          <PrimaryMetric
            label="Amount"
            tooltip="Sum of pre-tax totals"
            value={tileValue(bucket?.subtotal ?? '')}
            color="success.dark"
            isLoading={bucket === null}
            caption={
              bucket === null
                ? undefined
                : `${tileValue(bucket.total)} total (with tax)`
            }
          />
        </SummaryCard>
      </Grid>
      <Grid item xs={12} md={4}>
        <SummaryCard>
          <PrimaryMetric
            label="Posted"
            tooltip="Lines on issued invoices (open, partially paid, or paid), reflecting the invoiced amount that hits A/R and deferred/accrued revenue"
            value={tileValue(bucket?.postedAmount ?? '')}
            color="success.dark"
            isLoading={bucket === null}
            caption={
              bucket === null
                ? undefined
                : `${bucket.postedCount.toLocaleString()} of ${bucket.lineItemCount.toLocaleString()} line items`
            }
          />
          <MetricDivider>
            <MetricTable
              rows={buildStatusMixRows(bucket)}
              isLoading={bucket === null}
            />
          </MetricDivider>
        </SummaryCard>
      </Grid>
    </Grid>
  );
}

// Builds the rows for the status-mix card's MetricTable. Posted bundles
// OPEN/PARTIALLY_PAID/PAID — those are the invoices that have actually
// hit the GL via AR + deferred. Draft hasn't posted yet; Void was reversed.
// Semantic colors let a controller scan the column at a glance: Posted is
// the "GL-tied baseline" (success.dark), Draft is notable but expected
// (warning.dark), Void is muted when zero and alarms in red when non-zero
// since voids are an investigation trigger. Returns skeleton placeholders
// when the bucket hasn't loaded so the card keeps its 3-row shape during
// fetch.
function buildStatusMixRows(
  bucket: CurrencyInvoiceLineItemSummary | null
): MetricTableRow[] {
  if (bucket === null) {
    return [
      { label: 'Posted', count: '', amount: '' },
      { label: 'Draft', count: '', amount: '' },
      { label: 'Void', count: '', amount: '' },
    ];
  }
  const voidColor = bucket.voidCount > 0 ? 'error.main' : 'text.disabled';
  const currencyCode = bucket.isoCode;
  function amount(value: string): string {
    return formatCurrencyValue({
      amount: new Decimal(value),
      isoCode: currencyCode,
    });
  }
  return [
    {
      label: 'Posted',
      count: bucket.postedCount.toLocaleString(),
      amount: amount(bucket.postedAmount),
      color: 'success.dark',
    },
    {
      label: 'Draft',
      count: bucket.draftCount.toLocaleString(),
      amount: amount(bucket.draftAmount),
      color: 'warning.dark',
    },
    {
      label: 'Void',
      count: bucket.voidCount.toLocaleString(),
      amount: amount(bucket.voidAmount),
      color: voidColor,
    },
  ];
}

function SortableHeader(props: {
  label: string;
  sortProperty: InvoiceLineItemSortProperty;
  width: string;
  align?: 'right';
  getSortDirection: (
    property: InvoiceLineItemSortProperty
  ) => false | SortDirection;
  getLabelDirection: (
    property: InvoiceLineItemSortProperty
  ) => SortDirection | undefined;
  isLabelActive: (property: InvoiceLineItemSortProperty) => boolean;
  onSort: (property: InvoiceLineItemSortProperty) => () => void;
}) {
  return (
    <TableCell
      width={props.width}
      align={props.align}
      sortDirection={props.getSortDirection(props.sortProperty)}
    >
      <TableSortLabel
        active={props.isLabelActive(props.sortProperty)}
        direction={props.getLabelDirection(props.sortProperty) ?? 'asc'}
        onClick={props.onSort(props.sortProperty)}
      >
        {props.label}
      </TableSortLabel>
    </TableCell>
  );
}

function InvoiceLineItemRow(props: { lineItem: InvoiceLineItemListItem }) {
  const { lineItem } = props;
  const servicePeriod =
    lineItem.servicePeriodStart && lineItem.servicePeriodEnd
      ? `${formatShortDate(lineItem.servicePeriodStart)} – ${formatShortDate(lineItem.servicePeriodEnd)}`
      : '—';
  const itemPricingCaption =
    lineItem.pricingMode === 'PER_UNIT' && lineItem.unitPrice !== null
      ? `${lineItem.quantity} × ${formatCurrencyValue(lineItem.unitPrice)}`
      : `${formatCurrencyValue(lineItem.totalPrice)} fixed`;
  const taxAmount = lineItem.taxAmount;
  const sentDate = lineItem.firstSentAt
    ? formatShortDate(lineItem.firstSentAt)
    : '—';
  const paidDate = lineItem.paidDate ? formatShortDate(lineItem.paidDate) : '—';

  return (
    <TableRow hover>
      <TableCell>
        <Stack spacing={0.5} direction="row" alignItems="center">
          <Link
            to="/billing/invoices/$invoiceKey"
            params={{ invoiceKey: lineItem.invoiceKey }}
          >
            {lineItem.invoiceNumber}
          </Link>
          <InvoiceStatusChip status={lineItem.invoiceStatus} />
        </Stack>
      </TableCell>
      <TableCell>
        <Typography variant="body2">{lineItem.customerName ?? '—'}</Typography>
      </TableCell>
      <TableCell>
        {lineItem.projectKey && lineItem.projectName ? (
          <Link
            to="/projects/$projectKey"
            params={{ projectKey: lineItem.projectKey }}
          >
            {lineItem.projectName}
          </Link>
        ) : (
          '—'
        )}
      </TableCell>
      <TableCell>
        <Typography variant="body2">{lineItem.description}</Typography>
      </TableCell>
      <TableCell>
        <Stack spacing={0.25}>
          <Tooltip
            title={lineItem.itemName ?? lineItem.description}
            arrow
            placement="top"
          >
            <Typography
              variant="body2"
              component="span"
              sx={{
                display: 'inline-block',
                alignSelf: 'flex-start',
                borderBottom: '1px dotted',
                borderColor: 'text.secondary',
              }}
            >
              {lineItem.itemSku ?? '—'}
            </Typography>
          </Tooltip>
          <Typography variant="caption" color="text.secondary">
            {itemPricingCaption}
          </Typography>
        </Stack>
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2" fontWeight={500}>
          {formatCurrencyValue(lineItem.totalPrice)}
        </Typography>
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2" color="text.secondary">
          {formatCurrencyValue(taxAmount)}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="text.secondary">
          {servicePeriod}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="text.secondary">
          {sentDate}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="text.secondary">
          {paidDate}
        </Typography>
      </TableCell>
    </TableRow>
  );
}
